import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Calendar from './Calendar';

import MarksheetTemplateManager from './MarksheetTemplateManager';
import FileManager from './FileManager';
import DiaryTemplateManager from './DiaryTemplateManager';
import WhatsAppLinksManager from './WhatsAppLinksManager';
import ClassStructureManager from './ClassStructureManager';
import TuitionStudentManager from './TuitionStudentManager';
import TeacherAttendanceView from './TeacherAttendanceView';
import TimetableManager from './TimetableManager';
import GamesManager from './Games/GamesManager';
import AcademicStructureManager from './AcademicStructureManager';
import AttendanceLauncher from './AttendanceMaster/AttendanceLauncher';
import AdminStreamControl from './LiveStream/AdminStreamControl';
import BulkDataManager from './BulkDataManager';
import DiaryManagerPanel from './DiaryManagerPanel';

function Admin1Dashboard({ user, onLogout }) {
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ 
    name: '', 
    email: '', 
    password: '', 
    role: 'student', 
    class: '', 
    fathersName: '', 
    studentId: '',
    profilePicture: ''
  });
  const [classes, setClasses] = useState([]);
  const [academicYear, setAcademicYear] = useState('2025-26');
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedSection, setSelectedSection] = useState('all');
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [sections] = useState(['A', 'B', 'C', 'D']);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTeachers, setFilteredTeachers] = useState([]);
  const [showTeacherAttendance, setShowTeacherAttendance] = useState(false);
  const [teacherAttendanceData, setTeacherAttendanceData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showMarksheetTemplateManager, setShowMarksheetTemplateManager] = useState(false);
  const [showFileManager, setShowFileManager] = useState(false);
  const [showDiaryTemplateManager, setShowDiaryTemplateManager] = useState(false);
  const [showWhatsAppLinksManager, setShowWhatsAppLinksManager] = useState(false);
  const [showClassStructureManager, setShowClassStructureManager] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showTuitionStudentManager, setShowTuitionStudentManager] = useState(false);
  const [selectedTuitionTeacher, setSelectedTuitionTeacher] = useState(null);
  const [showTeacherAttendanceView, setShowTeacherAttendanceView] = useState(false);
  const [selectedTeacherForAttendance, setSelectedTeacherForAttendance] = useState(null);
  const [showTimetableManager, setShowTimetableManager] = useState(false);
  const [showGamesManager, setShowGamesManager] = useState(false);
  const [showAcademicStructureManager, setShowAcademicStructureManager] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchClasses();
    fetchTeacherAttendance();
  }, []);

  const fetchTeacherAttendance = async () => {
    try {
      console.log('Fetching teacher attendance for date:', selectedDate);
      const response = await axios.get(`/api/teacher-attendance?date=${selectedDate}`);
      console.log('Teacher attendance data received:', response.data);
      setTeacherAttendanceData(response.data || []);
    } catch (error) {
      console.error('Error fetching teacher attendance:', error);
      console.error('Error details:', error.response?.data);
      setTeacherAttendanceData([]);
    }
  };

  const markTeacherAttendance = async (teacherId, teacherName, status, remarks = '') => {
    try {
      console.log('Marking attendance:', { teacherId, teacherName, date: selectedDate, status, markedBy: user.name });
      const response = await axios.post('/api/teacher-attendance', {
        teacherId,
        teacherName,
        date: selectedDate,
        status,
        markedBy: user.name,
        remarks
      });
      console.log('Attendance marked successfully:', response.data);
      await fetchTeacherAttendance();
      alert(`Teacher attendance marked as ${status}!`);
    } catch (error) {
      console.error('Error marking teacher attendance:', error);
      console.error('Error details:', error.response?.data);
      alert(`Error marking attendance: ${error.response?.data?.error || error.message}`);
    }
  };

  const getTeacherAttendanceStatus = (teacherId) => {
    if (!Array.isArray(teacherAttendanceData)) return null;
    const record = teacherAttendanceData.find(att => att.teacherId === teacherId && att.date === selectedDate);
    return record ? record.status : null;
  };

  useEffect(() => {
    filterStudentsByClass();
    filterTeachers();
  }, [users, selectedClass, selectedSection, searchTerm]);

  useEffect(() => {
    if (selectedDate) {
      fetchTeacherAttendance();
    }
  }, [selectedDate]);

  const filterStudentsByClass = () => {
    let students = users.filter(u => u.role === 'student');
    
    if (selectedClass !== 'all') {
      students = students.filter(s => s.class === selectedClass);
    }
    
    if (selectedSection !== 'all') {
      students = students.filter(s => s.section === selectedSection);
    }
    
    if (searchTerm) {
      students = students.filter(s => 
        s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (s.studentId && s.studentId.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (s.fathersName && s.fathersName.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    setFilteredStudents(students);
  };
  
  const filterTeachers = () => {
    let teachers = users.filter(u => u.role === 'teacher' || u.role === 'teacher_admin_level1' || u.role === 'tuition_teacher');
    
    if (searchTerm) {
      teachers = teachers.filter(t => 
        t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (t.class && t.class.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    setFilteredTeachers(teachers);
  };

  const getStudentCountByClass = (className) => {
    return users.filter(u => u.role === 'student' && u.class === className).length;
  };

  const getStudentCountBySection = (className, sectionName) => {
    return users.filter(u => u.role === 'student' && u.class === className && u.section === sectionName).length;
  };

  const fetchClasses = async () => {
    try {
      const response = await axios.get('/api/classes');
      if (response.data.length === 0) {
        // Initialize with default classes if none exist
        const defaultClasses = ['10A', '10B', '11A', '11B', '12A', '12B'];
        for (const className of defaultClasses) {
          await axios.post('/api/classes', { name: className });
        }
        setClasses(defaultClasses);
      } else {
        setClasses(response.data.map(cls => cls.name));
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      // Fallback to default classes
      setClasses(['10A', '10B', '11A', '11B', '12A', '12B']);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/users', newUser);
      setNewUser({ 
        name: '', 
        email: '', 
        password: '', 
        role: 'student', 
        class: '', 
        fathersName: '', 
        studentId: '',
        profilePicture: ''
      });
      fetchUsers();
      alert('User added successfully!');
    } catch (error) {
      console.error('Error adding user:', error);
    }
  };

  const deleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await axios.delete(`/api/users/${userId}`);
        fetchUsers();
        alert('User deleted successfully!');
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const assignToClass = async (userId, className, sectionName = null) => {
    try {
      const updateData = { class: className };
      if (sectionName !== null) {
        updateData.section = sectionName;
      }
      await axios.put(`/api/users/${userId}`, updateData);
      fetchUsers();
      alert(`User assigned to ${className}${sectionName ? ` - Section ${sectionName}` : ''} successfully!`);
    } catch (error) {
      console.error('Error assigning user:', error);
    }
  };

  const sendAnnouncement = async () => {
    const textarea = document.querySelector('#school-announcement-textarea');
    const titleInput = document.querySelector('#announcement-title');
    if (textarea && textarea.value.trim()) {
      try {
        const response = await axios.post('/api/announcements', {
          message: textarea.value,
          type: 'school',
          createdBy: user.name || 'Admin'
        });
        console.log('Announcement sent:', response.data);
        alert(`School-wide announcement sent successfully!`);
        textarea.value = '';
        if (titleInput) titleInput.value = '';
      } catch (error) {
        console.error('Error sending announcement:', error);
        alert('Error sending announcement');
      }
    } else {
      alert('Please write an announcement first');
    }
  };

  const generateReport = (type) => {
    alert(`${type} report generated successfully! Check your downloads.`);
  };

  const addClass = async () => {
    const className = prompt('Enter new class name (e.g., 12C):');
    if (className && className.trim()) {
      if (classes.includes(className.trim())) {
        alert('Class already exists!');
        return;
      }
      try {
        await axios.post('/api/classes', { name: className.trim() });
        setClasses([...classes, className.trim()]);
        alert(`Class ${className} added successfully!`);
      } catch (error) {
        console.error('Error adding class:', error);
        const errorMsg = error.response?.data?.message || 'Error adding class. Please try again.';
        alert(errorMsg);
      }
    }
  };

  const manageSections = () => {
    alert('Section management panel opened!');
  };

  const viewContent = () => {
    alert('All class content displayed for review!');
  };

  const manageCalendar = () => {
    alert('Academic calendar management opened!');
  };

  const scheduleEvent = () => {
    const event = prompt('Enter event name:');
    const date = prompt('Enter date (YYYY-MM-DD):');
    if (event && date) {
      alert(`Event "${event}" scheduled for ${date}`);
    }
  };

  const updateAcademicYear = () => {
    const newYear = prompt('Enter new academic year (e.g., 2025-26):', academicYear);
    if (newYear && newYear !== academicYear) {
      setAcademicYear(newYear);
      alert(`Academic year updated to ${newYear} for all students`);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size before upload
      if (file.size > 1024 * 1024) { // 1MB in bytes
        alert('File size exceeds 1MB limit. Please choose a smaller file.');
        e.target.value = ''; // Clear the input
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('uploadedBy', user.name);
      formData.append('uploaderRole', user.role);
      formData.append('uploadType', 'profile');
      
      try {
        const response = await axios.post('/api/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setNewUser({...newUser, profilePicture: `/uploads/${response.data.filename}`});
        alert('Image uploaded successfully!');
      } catch (error) {
        console.error('Error uploading image:', error);
        const errorMessage = error.response?.data?.message || error.message;
        if (errorMessage.includes('1MB') || errorMessage.includes('file size')) {
          alert('File size exceeds 1MB limit. Please choose a smaller file.');
        } else {
          alert('Error uploading image: ' + errorMessage);
        }
        e.target.value = ''; // Clear the input on error
      }
    }
  };

  const editUserProfile = (user) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.png,.jpg,.jpeg';
    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        // Check file size before upload
        if (file.size > 1024 * 1024) { // 1MB in bytes
          alert('File size exceeds 1MB limit. Please choose a smaller file.');
          return;
        }

        const formData = new FormData();
        formData.append('profilePicture', file);
        formData.append('userId', user._id);
        
        try {
          console.log('Uploading profile picture for user:', user._id);
          const response = await axios.post('/api/upload-profile', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          console.log('Upload response:', response.data);
          fetchUsers();
          alert('Profile picture updated successfully!');
        } catch (error) {
          console.error('Error updating profile picture:', error);
          const errorMessage = error.response?.data?.message || error.message;
          if (errorMessage.includes('1MB') || errorMessage.includes('file size')) {
            alert('File size exceeds 1MB limit. Please choose a smaller file.');
          } else {
            alert('Error updating profile picture: ' + errorMessage);
          }
        }
      }
    };
    fileInput.click();
  };

  const editUserName = async (user) => {
    const newName = prompt(`Edit name for ${user.name}:`, user.name);
    if (newName && newName.trim() && newName.trim() !== user.name) {
      try {
        await axios.put(`/api/users/${user._id}`, { name: newName.trim() });
        fetchUsers();
        alert('Name updated successfully!');
      } catch (error) {
        console.error('Error updating name:', error);
        alert('Error updating name: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const editUserData = async (user) => {
    const editForm = document.createElement('div');
    editForm.innerHTML = `
      <div style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; justify-content: center; align-items: center;">
        <div style="background: white; padding: 30px; border-radius: 15px; width: 90%; max-width: 500px; max-height: 90%; overflow-y: auto; box-shadow: 0 10px 30px rgba(0,0,0,0.3);">
          <h3 style="margin-bottom: 20px; color: #333; text-align: center;">✏️ Edit ${user.role === 'student' ? 'Student' : 'Teacher'} Data</h3>
          <form id="editUserForm">
            <div style="margin-bottom: 15px;">
              <label style="display: block; margin-bottom: 5px; font-weight: bold;">Name:</label>
              <input type="text" id="editName" value="${user.name || ''}" style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 5px;" required />
            </div>
            <div style="margin-bottom: 15px;">
              <label style="display: block; margin-bottom: 5px; font-weight: bold;">Email:</label>
              <input type="email" id="editEmail" value="${user.email || ''}" style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 5px;" required />
            </div>
            <div style="margin-bottom: 15px;">
              <label style="display: block; margin-bottom: 5px; font-weight: bold;">Password:</label>
              <div style="position: relative; display: flex; align-items: center;">
                <input type="password" id="editPassword" value="${user.password || ''}" style="width: 100%; padding: 8px 40px 8px 8px; border: 1px solid #ddd; border-radius: 5px;" />
                <button type="button" onclick="toggleEditPassword()" style="position: absolute; right: 10px; background: none; border: none; cursor: pointer; font-size: 16px; color: #666; padding: 0;" title="Toggle password visibility">
                  👁️
                </button>
              </div>
              <small style="color: #666;">Leave blank to keep current password</small>
            </div>
            <div style="margin-bottom: 15px;">
              <label style="display: block; margin-bottom: 5px; font-weight: bold;">Class:</label>
              <select id="editClass" style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 5px;">
                <option value="">Select Class</option>
                ${classes.map(cls => `<option value="${cls}" ${user.class === cls ? 'selected' : ''}>${cls}</option>`).join('')}
              </select>
            </div>
            <div style="margin-bottom: 15px;">
              <label style="display: block; margin-bottom: 5px; font-weight: bold;">Section:</label>
              <select id="editSection" style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 5px;">
                <option value="">No Section</option>
                ${sections.map(section => `<option value="${section}" ${user.section === section ? 'selected' : ''}>Section ${section}</option>`).join('')}
              </select>
            </div>
            ${user.role === 'student' ? `
              <div style="margin-bottom: 15px;">
                <label style="display: block; margin-bottom: 5px; font-weight: bold;">Father's Name:</label>
                <input type="text" id="editFathersName" value="${user.fathersName || ''}" style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 5px;" />
              </div>
              <div style="margin-bottom: 15px;">
                <label style="display: block; margin-bottom: 5px; font-weight: bold;">Student ID:</label>
                <input type="text" id="editStudentId" value="${user.studentId || ''}" style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 5px;" />
              </div>
              <div style="margin-bottom: 15px;">
                <label style="display: block; margin-bottom: 5px; font-weight: bold;">Class Rank:</label>
                <input type="text" id="editClassRank" value="${user.classRank || ''}" style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 5px;" placeholder="e.g., 1st, 2nd, 3rd" />
              </div>
            ` : ''}
            <div style="display: flex; gap: 10px; justify-content: center; margin-top: 20px;">
              <button type="submit" style="padding: 10px 20px; background: #28a745; color: white; border: none; border-radius: 5px; cursor: pointer; font-weight: bold;">💾 Save Changes</button>
              <button type="button" id="cancelEdit" style="padding: 10px 20px; background: #dc3545; color: white; border: none; border-radius: 5px; cursor: pointer; font-weight: bold;">❌ Cancel</button>
            </div>
          </form>
        </div>
      </div>
    `;
    
    document.body.appendChild(editForm);
    
    // Add toggle password function to window for the edit form
    window.toggleEditPassword = () => {
      const passwordInput = document.getElementById('editPassword');
      const toggleButton = passwordInput.nextElementSibling;
      if (passwordInput.type === 'password') {
        passwordInput.type = 'text';
        toggleButton.innerHTML = '🙈';
        toggleButton.title = 'Hide password';
      } else {
        passwordInput.type = 'password';
        toggleButton.innerHTML = '👁️';
        toggleButton.title = 'Show password';
      }
    };
    
    const form = document.getElementById('editUserForm');
    const cancelBtn = document.getElementById('cancelEdit');
    
    cancelBtn.onclick = () => {
      document.body.removeChild(editForm);
      // Clean up the global function
      delete window.toggleEditPassword;
    };
    
    form.onsubmit = async (e) => {
      e.preventDefault();
      
      const updateData = {
        name: document.getElementById('editName').value.trim(),
        email: document.getElementById('editEmail').value.trim(),
        class: document.getElementById('editClass').value,
        section: document.getElementById('editSection').value
      };
      
      const password = document.getElementById('editPassword').value.trim();
      if (password) {
        updateData.password = password;
      }
      
      if (user.role === 'student') {
        updateData.fathersName = document.getElementById('editFathersName').value.trim();
        updateData.studentId = document.getElementById('editStudentId').value.trim();
        updateData.classRank = document.getElementById('editClassRank').value.trim();
      }
      
      try {
        await axios.put(`/api/users/${user._id}`, updateData);
        fetchUsers();
        document.body.removeChild(editForm);
        alert(`${user.role === 'student' ? 'Student' : 'Teacher'} data updated successfully!`);
      } catch (error) {
        console.error('Error updating user data:', error);
        alert('Error updating user data: ' + (error.response?.data?.message || error.message));
      }
    };
  };

  const manageTuitionStudents = (tuitionTeacher) => {
    setSelectedTuitionTeacher(tuitionTeacher);
    setShowTuitionStudentManager(true);
  };

  return (
    <div className="dashboard admin1-theme">
      <div style={{ 
        padding: window.innerWidth <= 768 ? '8px 12px' : '10px 20px', 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', 
        borderBottom: '2px solid #dee2e6', 
        position: 'relative', 
        zIndex: 9999 
      }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: window.innerWidth <= 768 ? 'flex-start' : 'center',
          gap: window.innerWidth <= 768 ? '10px' : '0'
        }}>
          <div>
            <h3 style={{ 
              margin: 0, 
              color: '#495057', 
              fontSize: window.innerWidth <= 768 ? '16px' : '18px', 
              fontWeight: '600' 
            }}>📅 Event Management Calendar</h3>
            <p style={{ 
              margin: '5px 0 0 0', 
              color: '#495057', 
              fontSize: window.innerWidth <= 768 ? '14px' : '16px', 
              fontWeight: 'bold', 
              textShadow: '0 1px 2px rgba(0,0,0,0.1)' 
            }}>{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
        </div>
        <div style={{ 
          marginTop: '15px', 
          background: '#f0f8ff', 
          padding: window.innerWidth <= 768 ? '8px' : '10px', 
          borderRadius: '8px' 
        }}>
          <Calendar 
            userRole="admin1"
            userClass="all" 
            userName={user.name} 
            inline={true} 
          />
        </div>
      </div>
      
      <div className="header" style={{
        flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
        gap: window.innerWidth <= 768 ? '15px' : '0',
        padding: window.innerWidth <= 768 ? '15px' : '15px 20px'
      }}>
        <div className="profile-section" style={{
          flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
          textAlign: window.innerWidth <= 768 ? 'center' : 'left',
          gap: window.innerWidth <= 768 ? '10px' : '15px'
        }}>
          <div className="profile-pic" style={{
            width: window.innerWidth <= 768 ? '60px' : '50px',
            height: window.innerWidth <= 768 ? '60px' : '50px',
            fontSize: window.innerWidth <= 768 ? '24px' : '20px'
          }}>🛠️</div>
          <div>
            <h3 style={{ fontSize: window.innerWidth <= 768 ? '20px' : '24px' }}>{user.name}</h3>
            <p style={{ fontSize: window.innerWidth <= 768 ? '14px' : '16px' }}>Admin Level 1</p>
          </div>
        </div>
        <button 
          className="logout-btn" 
          onClick={onLogout}
          style={{
            padding: window.innerWidth <= 768 ? '10px 20px' : '8px 16px',
            fontSize: window.innerWidth <= 768 ? '16px' : '14px',
            minHeight: window.innerWidth <= 768 ? '44px' : 'auto'
          }}
        >Logout</button>
      </div>

      <div className="dashboard-grid" style={{
        gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: window.innerWidth <= 768 ? '15px' : '20px'
      }}>
        <AttendanceLauncher user={user} />
        {['principal', 'admin', 'admin1'].includes(user?.role) && (
          <div id="bulk-data-manager" style={{ gridColumn: '1 / -1' }}>
            <BulkDataManager user={user} />
          </div>
        )}
        {['principal', 'admin', 'admin1'].includes(user?.role) && (
          <div id="diary-manager" style={{ gridColumn: '1 / -1' }}>
            <DiaryManagerPanel user={user} />
          </div>
        )}
        {['principal', 'admin', 'admin1', 'admin2'].includes(user?.role) && (
          <div id="admin-stream-control" style={{ gridColumn: '1 / -1' }}>
            <AdminStreamControl />
          </div>
        )}
        <div className="card" style={{
          padding: window.innerWidth <= 768 ? '15px' : '20px'
        }}>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
            marginBottom: window.innerWidth <= 768 ? '12px' : '15px'
          }}>➕ Admit New Student/Teacher</h3>
          <form onSubmit={handleAddUser}>
            <div className="form-group">
              <label>Name:</label>
              <input
                type="text"
                value={newUser.name}
                onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Email:</label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Password:</label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  required
                  style={{ paddingRight: '40px', width: '100%' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '16px',
                    color: '#666',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label>Role:</label>
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({...newUser, role: e.target.value})}
              >
                <option value="student">Student</option>
                <option value="teacher">Teacher</option>
                <option value="teacher_admin_level1">Teacher Admin Level 1 (Super Teacher)</option>
                <option value="tuition_teacher">Tuition Teacher</option>
              </select>
            </div>
            <div className="form-group">
              <label>Assign to Class:</label>
              <select
                value={newUser.class}
                onChange={(e) => setNewUser({...newUser, class: e.target.value})}
              >
                <option value="">Select Class</option>
                {classes.map(cls => (
                  <option key={cls} value={cls}>{cls}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Profile Picture (PNG):</label>
              <input
                type="file"
                accept=".png,.jpg,.jpeg"
                onChange={(e) => handleImageUpload(e)}
              />
            </div>
            
            {newUser.role === 'teacher_admin_level1' && (
              <div style={{ marginBottom: '15px', padding: '15px', background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)', borderRadius: '10px', border: '2px solid #9c27b0' }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#6a1b9a' }}>🎭 Teacher Admin Level 1 - Super Teacher</h4>
                <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '14px', color: '#6a1b9a' }}>
                  <li>Can access any teacher's account directly (impersonation)</li>
                  <li>Automatically recognized across all teacher accounts</li>
                  <li>Can manage roles and permissions for teachers</li>
                  <li>Full authority over teacher account management</li>
                  <li>Maintains same view as normal teacher when impersonating</li>
                </ul>
              </div>
            )}
            
            {newUser.role === 'tuition_teacher' && (
              <div style={{ marginBottom: '15px', padding: '15px', background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)', borderRadius: '10px', border: '2px solid #4caf50' }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#2e7d32' }}>🎯 Tuition Teacher</h4>
                <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '14px', color: '#2e7d32' }}>
                  <li>Can access assigned students from any class</li>
                  <li>Cross-class student management capabilities</li>
                  <li>Full impersonation access to assigned student accounts</li>
                  <li>Admin Level 1 assigns specific students to tuition teacher</li>
                  <li>Perfect for private tutoring and specialized instruction</li>
                </ul>
              </div>
            )}
            
            {newUser.role === 'student' && (
              <>
                <div className="form-group">
                  <label>Father's Name:</label>
                  <input
                    type="text"
                    value={newUser.fathersName}
                    onChange={(e) => setNewUser({...newUser, fathersName: e.target.value})}
                    placeholder="Enter father's name"
                  />
                </div>

                <div className="form-group">
                  <label>Student ID:</label>
                  <input
                    type="text"
                    value={newUser.studentId}
                    onChange={(e) => setNewUser({...newUser, studentId: e.target.value})}
                    placeholder="Enter unique student ID"
                  />
                </div>
              </>
            )}
            
            <button type="submit" className="btn">Add User</button>
          </form>
        </div>

        <div className="card" style={{ 
          gridColumn: window.innerWidth <= 768 ? 'span 1' : 'span 2',
          padding: window.innerWidth <= 768 ? '15px' : '20px'
        }}>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
            marginBottom: window.innerWidth <= 768 ? '12px' : '15px'
          }}>🎓 Student Management System</h3>
          
          {/* Search Bar */}
          <div style={{ 
            marginBottom: '20px', 
            padding: window.innerWidth <= 768 ? '12px' : '15px', 
            background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)', 
            borderRadius: '10px', 
            border: '2px solid #ff9800' 
          }}>
            <div style={{ 
              display: 'flex', 
              flexDirection: window.innerWidth <= 480 ? 'column' : 'row',
              alignItems: window.innerWidth <= 480 ? 'stretch' : 'center', 
              gap: window.innerWidth <= 480 ? '8px' : '10px' 
            }}>
              <label style={{ 
                fontWeight: 'bold', 
                color: '#e65100', 
                fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                minWidth: window.innerWidth <= 480 ? 'auto' : 'fit-content'
              }}>🔍 Search:</label>
              <input 
                type="text" 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={window.innerWidth <= 480 ? "Search..." : "Search by name, email, student ID, or father's name..."}
                style={{ 
                  flex: 1, 
                  padding: window.innerWidth <= 768 ? '8px 12px' : '10px 15px', 
                  border: '2px solid #ff9800', 
                  borderRadius: '8px', 
                  fontSize: window.innerWidth <= 768 ? '14px' : '14px',
                  background: 'white',
                  minHeight: window.innerWidth <= 768 ? '40px' : 'auto'
                }}
              />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm('')}
                  style={{ 
                    padding: '8px 12px', 
                    background: '#ff9800', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '6px', 
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  ✕ Clear
                </button>
              )}
            </div>
            {searchTerm && (
              <div style={{ marginTop: '10px', fontSize: '14px', color: '#e65100' }}>
                Found: {filteredStudents.length} students, {filteredTeachers.length} teachers
              </div>
            )}
          </div>
          
          {/* Class & Section Filter */}
          <div style={{ 
            marginBottom: '20px', 
            padding: window.innerWidth <= 768 ? '12px' : '15px', 
            background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', 
            borderRadius: '10px', 
            border: '2px solid #2196f3' 
          }}>
            <div style={{ 
              display: 'flex', 
              flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
              alignItems: window.innerWidth <= 768 ? 'stretch' : 'center', 
              gap: window.innerWidth <= 768 ? '12px' : '15px', 
              marginBottom: '15px', 
              flexWrap: 'wrap' 
            }}>
              <div style={{ 
                display: 'flex', 
                flexDirection: window.innerWidth <= 480 ? 'column' : 'row',
                alignItems: window.innerWidth <= 480 ? 'stretch' : 'center', 
                gap: window.innerWidth <= 480 ? '6px' : '10px',
                width: window.innerWidth <= 768 ? '100%' : 'auto'
              }}>
                <label style={{ 
                  fontWeight: 'bold', 
                  color: '#1565c0', 
                  fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                  minWidth: window.innerWidth <= 480 ? 'auto' : 'fit-content'
                }}>📚 Class:</label>
                <select 
                  value={selectedClass} 
                  onChange={(e) => {
                    setSelectedClass(e.target.value);
                    setSelectedSection('all');
                  }}
                  style={{ 
                    padding: window.innerWidth <= 768 ? '8px 12px' : '8px 15px', 
                    border: '2px solid #2196f3', 
                    borderRadius: '8px', 
                    fontSize: window.innerWidth <= 768 ? '13px' : '14px', 
                    fontWeight: 'bold',
                    background: 'white',
                    minWidth: window.innerWidth <= 768 ? '100%' : '180px',
                    minHeight: window.innerWidth <= 768 ? '40px' : 'auto'
                  }}
                >
                  <option value="all">All Classes ({users.filter(u => u.role === 'student').length})</option>
                  {classes.map(cls => (
                    <option key={cls} value={cls}>{cls} ({getStudentCountByClass(cls)} students)</option>
                  ))}
                  <option value="unassigned">Unassigned ({users.filter(u => u.role === 'student' && !u.class).length})</option>
                </select>
              </div>
              
              {selectedClass !== 'all' && selectedClass !== 'unassigned' && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: window.innerWidth <= 480 ? 'column' : 'row',
                  alignItems: window.innerWidth <= 480 ? 'stretch' : 'center', 
                  gap: window.innerWidth <= 480 ? '6px' : '10px',
                  width: window.innerWidth <= 768 ? '100%' : 'auto'
                }}>
                  <label style={{ 
                    fontWeight: 'bold', 
                    color: '#1565c0', 
                    fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                    minWidth: window.innerWidth <= 480 ? 'auto' : 'fit-content'
                  }}>📋 Section:</label>
                  <select 
                    value={selectedSection} 
                    onChange={(e) => setSelectedSection(e.target.value)}
                    style={{ 
                      padding: window.innerWidth <= 768 ? '8px 12px' : '8px 15px', 
                      border: '2px solid #2196f3', 
                      borderRadius: '8px', 
                      fontSize: window.innerWidth <= 768 ? '13px' : '14px', 
                      fontWeight: 'bold',
                      background: 'white',
                      minWidth: window.innerWidth <= 768 ? '100%' : '150px',
                      minHeight: window.innerWidth <= 768 ? '40px' : 'auto'
                    }}
                  >
                    <option value="all">All Sections ({getStudentCountByClass(selectedClass)})</option>
                    {sections.map(section => {
                      const count = getStudentCountBySection(selectedClass, section);
                      return count > 0 ? (
                        <option key={section} value={section}>Section {section} ({count})</option>
                      ) : null;
                    })}
                    <option value="no-section">No Section ({users.filter(u => u.role === 'student' && u.class === selectedClass && !u.section).length})</option>
                  </select>
                </div>
              )}
            </div>
            
            {/* Class Statistics */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: window.innerWidth <= 480 ? 'repeat(2, 1fr)' : window.innerWidth <= 768 ? 'repeat(3, 1fr)' : 'repeat(auto-fit, minmax(140px, 1fr))', 
              gap: window.innerWidth <= 768 ? '8px' : '10px' 
            }}>
              {classes.map(cls => {
                const count = getStudentCountByClass(cls);
                const isSelected = selectedClass === cls;
                return (
                  <div key={cls} style={{ 
                    padding: window.innerWidth <= 768 ? '8px' : '10px', 
                    background: isSelected ? '#2196f3' : 'rgba(33, 150, 243, 0.1)', 
                    color: isSelected ? 'white' : '#1565c0',
                    borderRadius: '8px', 
                    textAlign: 'center',
                    cursor: 'pointer',
                    border: '1px solid #2196f3',
                    transition: 'all 0.3s ease',
                    minHeight: window.innerWidth <= 768 ? '60px' : 'auto'
                  }}
                  onClick={() => {
                    setSelectedClass(cls);
                    setSelectedSection('all');
                  }}>
                    <div style={{ 
                      fontWeight: 'bold', 
                      fontSize: window.innerWidth <= 768 ? '12px' : '14px', 
                      marginBottom: '4px' 
                    }}>{cls}</div>
                    <div style={{ 
                      fontSize: window.innerWidth <= 768 ? '10px' : '12px', 
                      marginBottom: '6px' 
                    }}>{count} students</div>
                    <div style={{ 
                      fontSize: window.innerWidth <= 768 ? '8px' : '10px', 
                      display: 'flex', 
                      justifyContent: 'center', 
                      gap: '4px', 
                      flexWrap: 'wrap' 
                    }}>
                      {sections.map(section => {
                        const sectionCount = getStudentCountBySection(cls, section);
                        return sectionCount > 0 ? (
                          <span key={section} style={{ 
                            background: isSelected ? 'rgba(255,255,255,0.2)' : 'rgba(33, 150, 243, 0.2)', 
                            padding: '2px 4px', 
                            borderRadius: '3px',
                            fontSize: '9px'
                          }}>
                            {section}:{sectionCount}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Student List */}
          <div style={{ 
            maxHeight: window.innerWidth <= 768 ? '300px' : '400px', 
            overflowY: 'auto', 
            border: '1px solid #e0e0e0', 
            borderRadius: '8px', 
            background: '#fafafa' 
          }}>
            {filteredStudents.length > 0 ? (
              filteredStudents.map(student => (
                <div key={student._id} style={{ 
                  display: 'flex', 
                  flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                  justifyContent: 'space-between', 
                  alignItems: window.innerWidth <= 768 ? 'stretch' : 'center', 
                  marginBottom: '10px', 
                  padding: window.innerWidth <= 768 ? '12px' : '10px', 
                  background: 'white', 
                  borderRadius: '5px', 
                  margin: window.innerWidth <= 768 ? '8px' : '10px',
                  gap: window.innerWidth <= 768 ? '12px' : '0'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: window.innerWidth <= 768 ? '12px' : '10px',
                    flex: window.innerWidth <= 768 ? '1' : 'auto'
                  }}>
                    <div style={{ 
                      width: window.innerWidth <= 768 ? '60px' : '50px', 
                      height: window.innerWidth <= 768 ? '60px' : '50px', 
                      border: '2px solid #2196f3', 
                      background: student.profilePicture ? `url(${student.profilePicture})` : '#f0f0f0', 
                      backgroundSize: 'cover', 
                      backgroundPosition: 'center', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontSize: window.innerWidth <= 768 ? '20px' : '16px', 
                      borderRadius: '50%',
                      flexShrink: 0
                    }}>
                      {!student.profilePicture && '👤'}
                    </div>
                    <div>
                      <strong style={{ fontSize: window.innerWidth <= 768 ? '16px' : '14px' }}>{student.name}</strong>
                      <p style={{ 
                        margin: '2px 0', 
                        fontSize: window.innerWidth <= 768 ? '13px' : '12px' 
                      }}>Class: {student.class || 'Unassigned'}{student.section && ` - Section ${student.section}`}</p>
                      <p style={{ 
                        margin: '2px 0', 
                        fontSize: window.innerWidth <= 768 ? '12px' : '11px', 
                        color: '#666',
                        wordBreak: 'break-word'
                      }}>
                        {student.fathersName && `Father: ${student.fathersName}`}
                        {student.classRank && ` | Rank: ${student.classRank}`}
                        {student.studentId && ` | ID: ${student.studentId}`}
                      </p>
                      <small style={{ 
                        fontSize: window.innerWidth <= 768 ? '12px' : '11px',
                        wordBreak: 'break-word'
                      }}>{student.email}</small>
                    </div>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: window.innerWidth <= 768 ? 'row' : 'column', 
                    gap: window.innerWidth <= 768 ? '8px' : '5px',
                    flexWrap: window.innerWidth <= 768 ? 'wrap' : 'nowrap',
                    alignItems: window.innerWidth <= 768 ? 'center' : 'stretch'
                  }}>
                    <select 
                      onChange={(e) => assignToClass(student._id, e.target.value)} 
                      value={student.class || ''} 
                      style={{ 
                        padding: window.innerWidth <= 768 ? '6px 8px' : '4px', 
                        fontSize: window.innerWidth <= 768 ? '13px' : '12px',
                        minHeight: window.innerWidth <= 768 ? '36px' : 'auto',
                        flex: window.innerWidth <= 768 ? '1' : 'auto',
                        minWidth: window.innerWidth <= 768 ? '100px' : 'auto'
                      }}>
                      <option value="">Select Class</option>
                      {classes.map(cls => (
                        <option key={cls} value={cls}>{cls}</option>
                      ))}
                    </select>
                    {student.class && (
                      <select 
                        onChange={(e) => assignToClass(student._id, student.class, e.target.value)} 
                        value={student.section || ''} 
                        style={{ 
                          padding: window.innerWidth <= 768 ? '6px 8px' : '4px', 
                          fontSize: window.innerWidth <= 768 ? '13px' : '12px',
                          minHeight: window.innerWidth <= 768 ? '36px' : 'auto',
                          flex: window.innerWidth <= 768 ? '1' : 'auto',
                          minWidth: window.innerWidth <= 768 ? '100px' : 'auto'
                        }}>
                        <option value="">No Section</option>
                        {sections.map(section => (
                          <option key={section} value={section}>Section {section}</option>
                        ))}
                      </select>
                    )}
                    <div style={{ 
                      display: 'flex', 
                      gap: window.innerWidth <= 768 ? '6px' : '3px',
                      flexWrap: 'wrap',
                      justifyContent: window.innerWidth <= 768 ? 'flex-start' : 'center'
                    }}>
                      <button 
                        className="btn" 
                        style={{ 
                          fontSize: window.innerWidth <= 768 ? '12px' : '10px', 
                          padding: window.innerWidth <= 768 ? '6px 10px' : '2px 6px',
                          minHeight: window.innerWidth <= 768 ? '32px' : 'auto'
                        }} 
                        onClick={() => editUserData(student)} 
                        title="Edit all data"
                      >✏️</button>
                      <button 
                        className="btn" 
                        style={{ 
                          fontSize: window.innerWidth <= 768 ? '12px' : '10px', 
                          padding: window.innerWidth <= 768 ? '6px 10px' : '2px 6px',
                          minHeight: window.innerWidth <= 768 ? '32px' : 'auto'
                        }} 
                        onClick={() => editUserProfile(student)} 
                        title="Change photo"
                      >📷</button>
                      <button 
                        className="btn" 
                        style={{ 
                          background: '#dc3545', 
                          fontSize: window.innerWidth <= 768 ? '12px' : '10px', 
                          padding: window.innerWidth <= 768 ? '6px 10px' : '2px 6px',
                          minHeight: window.innerWidth <= 768 ? '32px' : 'auto'
                        }} 
                        onClick={() => deleteUser(student._id)} 
                        title="Delete user"
                      >🗑️</button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                <div style={{ fontSize: '48px', marginBottom: '10px' }}>📚</div>
                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '5px' }}>
                  {selectedClass === 'all' ? 'No students found' : `No students in ${selectedClass}`}
                  {selectedSection !== 'all' && ` - Section ${selectedSection}`}
                </div>
              </div>
            )}
          </div>
          
          <div style={{ marginTop: '15px', padding: '10px', background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong style={{ color: '#2e7d32' }}>
              Showing: {filteredStudents.length} students
              {selectedClass !== 'all' && ` in ${selectedClass}`}
              {selectedSection !== 'all' && selectedSection !== 'no-section' && ` - Section ${selectedSection}`}
              {searchTerm && ` matching "${searchTerm}"`}
            </strong>
            <div style={{ fontSize: '14px', color: '#2e7d32' }}>
              Total: {users.filter(u => u.role === 'student').length} students
            </div>
          </div>
          
          {/* Quick Actions Panel */}
          <div style={{ marginTop: '15px', padding: '15px', background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)', borderRadius: '10px', border: '2px solid #9c27b0' }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#6a1b9a' }}>⚡ Quick Actions for Admin Level 1</h4>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button 
                className="btn" 
                onClick={() => {
                  if (filteredStudents.length === 0) {
                    alert('No students to export!');
                    return;
                  }
                  const csvData = filteredStudents.map(s => `${s.name},${s.email},${s.class || 'Unassigned'},${s.section || 'No Section'},${s.fathersName || ''},${s.studentId || ''}`).join('\n');
                  const blob = new Blob([`Name,Email,Class,Section,Father's Name,Student ID\n${csvData}`], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `students_${selectedClass !== 'all' ? selectedClass : 'all'}_${new Date().toISOString().split('T')[0]}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                style={{ background: '#4caf50', fontSize: '12px', padding: '6px 12px' }}
              >
                📄 Export Students CSV
              </button>
              <button 
                className="btn" 
                onClick={() => {
                  const unassigned = users.filter(u => u.role === 'student' && !u.class).length;
                  alert(`Found ${unassigned} unassigned students. Use the class dropdown to assign them.`);
                }}
                style={{ background: '#ff9800', fontSize: '12px', padding: '6px 12px' }}
              >
                📊 Check Unassigned
              </button>
              <button 
                className="btn" 
                onClick={() => {
                  const incomplete = users.filter(u => u.role === 'student' && (!u.fathersName || !u.studentId)).length;
                  alert(`Found ${incomplete} students with incomplete data. Use the edit button to complete their information.`);
                }}
                style={{ background: '#f44336', fontSize: '12px', padding: '6px 12px' }}
              >
                ⚠️ Check Incomplete Data
              </button>
            </div>
          </div>
        </div>

        <div className="card" style={{
          padding: window.innerWidth <= 768 ? '15px' : '20px'
        }}>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
            marginBottom: window.innerWidth <= 768 ? '12px' : '15px'
          }}>👨🏫 Teacher Management & Attendance</h3>
          <div style={{ 
            marginBottom: '15px', 
            padding: window.innerWidth <= 768 ? '12px' : '10px', 
            background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)', 
            borderRadius: '8px', 
            border: '2px solid #4caf50' 
          }}>
            <div style={{ 
              display: 'flex', 
              flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
              justifyContent: 'space-between', 
              alignItems: window.innerWidth <= 768 ? 'stretch' : 'center', 
              marginBottom: '10px',
              gap: window.innerWidth <= 768 ? '10px' : '0'
            }}>
              <h4 style={{ 
                margin: 0, 
                color: '#2e7d32',
                fontSize: window.innerWidth <= 768 ? '16px' : '18px'
              }}>📅 Teacher Attendance - {selectedDate}</h4>
              <div style={{ 
                display: 'flex', 
                flexDirection: window.innerWidth <= 480 ? 'column' : 'row',
                gap: window.innerWidth <= 768 ? '8px' : '10px', 
                alignItems: window.innerWidth <= 480 ? 'stretch' : 'center' 
              }}>
                <input 
                  type="date" 
                  value={selectedDate}
                  onChange={(e) => {
                    setSelectedDate(e.target.value);
                    setTimeout(fetchTeacherAttendance, 100);
                  }}
                  style={{ 
                    padding: window.innerWidth <= 768 ? '8px' : '6px', 
                    border: '2px solid #4caf50', 
                    borderRadius: '6px', 
                    fontSize: window.innerWidth <= 768 ? '14px' : '12px',
                    minHeight: window.innerWidth <= 768 ? '40px' : 'auto'
                  }}
                />
                <button 
                  onClick={() => setShowTeacherAttendance(!showTeacherAttendance)}
                  style={{ 
                    background: showTeacherAttendance ? '#6c757d' : '#4caf50', 
                    color: 'white', 
                    border: 'none', 
                    padding: window.innerWidth <= 768 ? '10px 16px' : '6px 12px', 
                    borderRadius: '6px', 
                    cursor: 'pointer',
                    fontSize: window.innerWidth <= 768 ? '14px' : '12px',
                    fontWeight: 'bold',
                    minHeight: window.innerWidth <= 768 ? '40px' : 'auto'
                  }}
                >
                  {showTeacherAttendance ? 'Hide Attendance' : 'Take Attendance'}
                </button>
              </div>
            </div>
            {showTeacherAttendance && (
              <div style={{ background: 'white', padding: '10px', borderRadius: '6px', border: '1px solid #4caf50' }}>
                <div style={{ marginBottom: '15px', padding: '8px', background: '#f0f8ff', borderRadius: '6px', display: 'flex', justifyContent: 'space-around', fontSize: '12px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 'bold', color: '#4caf50' }}>{Array.isArray(teacherAttendanceData) ? teacherAttendanceData.filter(att => att.status === 'present' && att.date === selectedDate).length : 0}</div>
                    <div>Present</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 'bold', color: '#f44336' }}>{Array.isArray(teacherAttendanceData) ? teacherAttendanceData.filter(att => att.status === 'absent' && att.date === selectedDate).length : 0}</div>
                    <div>Absent</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 'bold', color: '#ff9800' }}>{Array.isArray(teacherAttendanceData) ? teacherAttendanceData.filter(att => att.status === 'late' && att.date === selectedDate).length : 0}</div>
                    <div>Late</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 'bold', color: '#9c27b0' }}>{Array.isArray(teacherAttendanceData) ? teacherAttendanceData.filter(att => att.status === 'first_half' && att.date === selectedDate).length : 0}</div>
                    <div>First Half</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 'bold', color: '#673ab7' }}>{Array.isArray(teacherAttendanceData) ? teacherAttendanceData.filter(att => att.status === 'second_half' && att.date === selectedDate).length : 0}</div>
                    <div>Second Half</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 'bold', color: '#2196f3' }}>{filteredTeachers.length}</div>
                    <div>Total</div>
                  </div>
                </div>
                <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#2e7d32', fontWeight: 'bold' }}>Mark attendance for all teachers:</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px' }}>
                  {filteredTeachers.map(teacher => {
                    const currentStatus = getTeacherAttendanceStatus(teacher._id);
                    return (
                      <div key={teacher._id} style={{ 
                        padding: '8px', 
                        background: '#f8f9fa', 
                        borderRadius: '6px', 
                        border: currentStatus ? '2px solid #4caf50' : '1px solid #ddd'
                      }}>
                        <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '5px' }}>{teacher.name}</div>
                        <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap' }}>
                          <button 
                            onClick={() => markTeacherAttendance(teacher._id, teacher.name, 'present')}
                            style={{ 
                              background: currentStatus === 'present' ? '#4caf50' : '#e0e0e0',
                              color: currentStatus === 'present' ? 'white' : '#666',
                              border: 'none', 
                              padding: '2px 4px', 
                              borderRadius: '3px', 
                              cursor: 'pointer',
                              fontSize: '9px',
                              fontWeight: 'bold'
                            }}
                          >
                            ✓ Present
                          </button>
                          <button 
                            onClick={() => markTeacherAttendance(teacher._id, teacher.name, 'absent')}
                            style={{ 
                              background: currentStatus === 'absent' ? '#f44336' : '#e0e0e0',
                              color: currentStatus === 'absent' ? 'white' : '#666',
                              border: 'none', 
                              padding: '2px 4px', 
                              borderRadius: '3px', 
                              cursor: 'pointer',
                              fontSize: '9px',
                              fontWeight: 'bold'
                            }}
                          >
                            ✗ Absent
                          </button>
                          <button 
                            onClick={() => markTeacherAttendance(teacher._id, teacher.name, 'late')}
                            style={{ 
                              background: currentStatus === 'late' ? '#ff9800' : '#e0e0e0',
                              color: currentStatus === 'late' ? 'white' : '#666',
                              border: 'none', 
                              padding: '2px 4px', 
                              borderRadius: '3px', 
                              cursor: 'pointer',
                              fontSize: '9px',
                              fontWeight: 'bold'
                            }}
                          >
                            ⏰ Late
                          </button>
                          <button 
                            onClick={() => markTeacherAttendance(teacher._id, teacher.name, 'first_half')}
                            style={{ 
                              background: currentStatus === 'first_half' ? '#9c27b0' : '#e0e0e0',
                              color: currentStatus === 'first_half' ? 'white' : '#666',
                              border: 'none', 
                              padding: '2px 4px', 
                              borderRadius: '3px', 
                              cursor: 'pointer',
                              fontSize: '9px',
                              fontWeight: 'bold'
                            }}
                          >
                            🌅 1st Half
                          </button>
                          <button 
                            onClick={() => markTeacherAttendance(teacher._id, teacher.name, 'second_half')}
                            style={{ 
                              background: currentStatus === 'second_half' ? '#673ab7' : '#e0e0e0',
                              color: currentStatus === 'second_half' ? 'white' : '#666',
                              border: 'none', 
                              padding: '2px 4px', 
                              borderRadius: '3px', 
                              cursor: 'pointer',
                              fontSize: '9px',
                              fontWeight: 'bold'
                            }}
                          >
                            🌆 2nd Half
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div>
            {filteredTeachers.map(teacher => (
              <div key={teacher._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', padding: '15px', background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', borderRadius: '10px', border: '1px solid #dee2e6' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <div style={{ width: '60px', height: '60px', border: '3px solid #007bff', background: teacher.profilePicture ? `url(${teacher.profilePicture})` : '#f0f0f0', backgroundSize: 'cover', backgroundPosition: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', borderRadius: '50%' }}>
                    {!teacher.profilePicture && '👨🏫'}
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <strong style={{ fontSize: '16px', color: '#333' }}>{teacher.name}</strong>
                      {teacher.role === 'teacher_admin_level1' && (
                        <span style={{ 
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                          color: 'white', 
                          padding: '2px 6px', 
                          borderRadius: '4px', 
                          fontSize: '10px', 
                          fontWeight: 'bold'
                        }}>
                          🎭 SUPER TEACHER
                        </span>
                      )}
                      {teacher.role === 'tuition_teacher' && (
                        <span style={{ 
                          background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)', 
                          color: 'white', 
                          padding: '2px 6px', 
                          borderRadius: '4px', 
                          fontSize: '10px', 
                          fontWeight: 'bold'
                        }}>
                          🎯 TUITION TEACHER
                        </span>
                      )}
                    </div>
                    <p style={{ margin: '4px 0', color: '#666' }}>
                      {teacher.role === 'tuition_teacher' ? (
                        <span>Tuition Teacher: <span style={{ fontWeight: 'bold', color: '#4caf50' }}>Cross-Class Access</span></span>
                      ) : (
                        <span>Assigned: <span style={{ fontWeight: 'bold', color: teacher.class ? '#28a745' : '#dc3545' }}>{teacher.class || 'No Class'}</span></span>
                      )}
                    </p>
                    <small style={{ color: '#6c757d' }}>{teacher.email}</small>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                  {teacher.role === 'tuition_teacher' ? (
                    <button 
                      onClick={() => manageTuitionStudents(teacher)}
                      style={{ 
                        padding: '6px 12px', 
                        background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)', 
                        color: 'white',
                        border: 'none', 
                        borderRadius: '6px', 
                        fontSize: '12px', 
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                    >
                      👥 Manage Students
                    </button>
                  ) : (
                    <select 
                      onChange={(e) => assignToClass(teacher._id, e.target.value)} 
                      value={teacher.class || ''} 
                      style={{ padding: '6px 10px', border: '2px solid #007bff', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold' }}
                    >
                      <option value="">Assign Class</option>
                      {classes.map(cls => (
                        <option key={cls} value={cls}>{cls}</option>
                      ))}
                    </select>
                  )}
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button 
                      onClick={() => {
                        setSelectedTeacherForAttendance(teacher);
                        setShowTeacherAttendanceView(true);
                      }}
                      style={{ 
                        background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)', 
                        color: 'white', 
                        border: 'none', 
                        padding: '4px 8px', 
                        borderRadius: '4px', 
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontWeight: 'bold'
                      }}
                      title="View monthly attendance"
                    >
                      📊
                    </button>
                    <button className="btn" style={{ fontSize: '11px', padding: '4px 8px', background: '#28a745' }} onClick={() => editUserData(teacher)} title="Edit all data">✏️ Edit</button>
                    <button className="btn" style={{ fontSize: '11px', padding: '4px 8px', background: '#17a2b8' }} onClick={() => editUserProfile(teacher)} title="Change photo">📷</button>
                    <button className="btn" style={{ background: '#dc3545', fontSize: '11px', padding: '4px 8px' }} onClick={() => deleteUser(teacher._id)} title="Delete user">🗑️</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '15px', padding: '10px', background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong style={{ color: '#1565c0' }}>Total Teachers: {users.filter(u => u.role === 'teacher' || u.role === 'teacher_admin_level1' || u.role === 'tuition_teacher').length}</strong>
            {searchTerm && (
              <span style={{ color: '#1565c0', fontSize: '14px' }}>Showing: {filteredTeachers.length} matching "{searchTerm}"</span>
            )}
          </div>
          
          {/* Teacher Quick Actions */}
          <div style={{ marginTop: '10px', padding: '10px', background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)', borderRadius: '8px', border: '1px solid #ffc107' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button 
                className="btn" 
                onClick={() => {
                  const unassigned = users.filter(u => (u.role === 'teacher' || u.role === 'teacher_admin_level1') && !u.class).length;
                  const tuitionTeachers = users.filter(u => u.role === 'tuition_teacher').length;
                  alert(`Found ${unassigned} teachers without class assignments.\nTuition Teachers: ${tuitionTeachers} (managed separately)`);
                }}
                style={{ background: '#ffc107', color: '#212529', fontSize: '11px', padding: '4px 8px' }}
              >
                📊 Check Unassigned Teachers
              </button>
              <button 
                className="btn" 
                onClick={() => {
                  const teacherData = filteredTeachers.map(t => `${t.name},${t.email},${t.class || 'Unassigned'}`).join('\n');
                  const blob = new Blob([`Name,Email,Assigned Class\n${teacherData}`], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `teachers_${new Date().toISOString().split('T')[0]}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                style={{ background: '#28a745', fontSize: '11px', padding: '4px 8px' }}
              >
                📄 Export Teachers CSV
              </button>
            </div>
          </div>
        </div>

        <div className="card" style={{
          padding: window.innerWidth <= 768 ? '15px' : '20px'
        }}>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
            marginBottom: window.innerWidth <= 768 ? '12px' : '15px'
          }}>🏫 Academic Structure Management</h3>
          <div style={{ marginBottom: '15px', padding: '15px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '10px', border: '2px solid #667eea' }}>
            <h4 style={{ margin: '0 0 10px 0', color: 'white' }}>⚡ Complete Academic Control</h4>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: 'white' }}>
              Manage the complete academic hierarchy: Subjects → Classes → Chapters → Topics → Question Papers
            </p>
            <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: 'white' }}>
              <li>Create and manage subjects (English, Maths, Science, etc.)</li>
              <li>Organize chapters and topics for each subject and class</li>
              <li>Upload question papers with JSON bulk import</li>
              <li>Assign papers to specific teachers</li>
            </ul>
          </div>
          <button 
            className="btn" 
            onClick={() => setShowAcademicStructureManager(true)}
            style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
              color: 'white',
              fontWeight: 'bold',
              padding: '12px 20px',
              fontSize: '16px',
              width: '100%',
              marginBottom: '15px'
            }}
          >
            🏫 Manage Academic Structure
          </button>
        </div>

        <div className="card" style={{
          padding: window.innerWidth <= 768 ? '15px' : '20px'
        }}>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
            marginBottom: window.innerWidth <= 768 ? '12px' : '15px'
          }}>🎮 Quiz Games Management</h3>
          <div style={{ marginBottom: '15px', padding: '15px', background: 'linear-gradient(135deg, #9C27B0 0%, #673AB7 100%)', borderRadius: '10px', border: '2px solid #9C27B0' }}>
            <h4 style={{ margin: '0 0 10px 0', color: 'white' }}>⚡ Admin Level 1 Quiz Control</h4>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: 'white' }}>
              Manage quiz questions across all subjects and classes. Upload questions in bulk via JSON files.
            </p>
            <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: 'white' }}>
              <li>Add/Edit/Delete quiz questions for all classes</li>
              <li>Bulk upload questions via JSON files</li>
              <li>Organize by subject, chapter, and difficulty</li>
              <li>Monitor student quiz performance</li>
            </ul>
          </div>
          <button 
            className="btn" 
            onClick={() => setShowGamesManager(true)}
            style={{ 
              background: 'linear-gradient(135deg, #9C27B0 0%, #673AB7 100%)', 
              color: 'white',
              fontWeight: 'bold',
              padding: '12px 20px',
              fontSize: '16px',
              width: '100%',
              marginBottom: '15px'
            }}
          >
            🎮 Manage Quiz Games
          </button>
        </div>

        <div className="card" style={{
          padding: window.innerWidth <= 768 ? '15px' : '20px'
        }}>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
            marginBottom: window.innerWidth <= 768 ? '12px' : '15px'
          }}>📅 Weekly Timetable Management</h3>
          <div style={{ marginBottom: '15px', padding: '15px', background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', borderRadius: '10px', border: '2px solid #2196f3' }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#1565c0' }}>⚡ Admin Level 1 Exclusive Control</h4>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#1565c0' }}>
              Create and manage weekly timetables for all classes. Set periods, subjects, and teacher assignments.
            </p>
            <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#1565c0' }}>
              <li>Create timetables for all classes</li>
              <li>Assign subjects and teachers to periods</li>
              <li>Set room allocations and timings</li>
              <li>View and edit existing timetables</li>
            </ul>
          </div>
          <button 
            className="btn" 
            onClick={() => setShowTimetableManager(true)}
            style={{ 
              background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)', 
              color: 'white',
              fontWeight: 'bold',
              padding: '12px 20px',
              fontSize: '16px',
              width: '100%',
              marginBottom: '15px'
            }}
          >
            📅 Manage Weekly Timetables
          </button>
        </div>

        <div className="card" style={{
          padding: window.innerWidth <= 768 ? '15px' : '20px'
        }}>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
            marginBottom: window.innerWidth <= 768 ? '12px' : '15px'
          }}>🗂️ Class Structure Management</h3>
          <div style={{ marginBottom: '15px', padding: '15px', background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)', borderRadius: '10px', border: '2px solid #4caf50' }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#2e7d32' }}>⚡ Admin Level 1 Exclusive Control</h4>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#2e7d32' }}>
              Complete control over class structure. Edit existing class names, add new classes, or delete unused ones.
            </p>
            <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#2e7d32' }}>
              <li>Edit class names (e.g., change "10A" to "Grade 10-A")</li>
              <li>Add new classes for new academic sessions</li>
              <li>Delete classes that are no longer needed</li>
              <li>View student count for each class</li>
            </ul>
          </div>
          <div style={{ marginBottom: '15px' }}>
            <h4>Current Classes: {classes.length}</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
              {classes.slice(0, 6).map(cls => (
                <div key={cls} style={{ padding: '10px', background: '#f8f9fa', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <div style={{ fontWeight: 'bold', color: '#495057', marginBottom: '4px' }}>{cls}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {users.filter(u => u.role === 'student' && u.class === cls).length} students
                  </div>
                </div>
              ))}
              {classes.length > 6 && (
                <div style={{ padding: '10px', background: '#e9ecef', borderRadius: '6px', border: '1px solid #dee2e6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                  +{classes.length - 6} more
                </div>
              )}
            </div>
          </div>
          <button 
            className="btn" 
            onClick={() => setShowClassStructureManager(true)}
            style={{ 
              background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)', 
              color: 'white',
              fontWeight: 'bold',
              padding: '12px 20px',
              fontSize: '16px',
              width: '100%',
              marginBottom: '10px'
            }}
          >
            🏫 Manage Class Structure
          </button>
          <button className="btn" onClick={manageSections}>Manage Sections</button>
        </div>

        <div className="card" style={{
          padding: window.innerWidth <= 768 ? '15px' : '20px'
        }}>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
            marginBottom: window.innerWidth <= 768 ? '12px' : '15px'
          }}>📋 Marksheet Pattern Control</h3>
          <div style={{ marginBottom: '15px', padding: '15px', background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)', borderRadius: '10px', border: '2px solid #ff9800' }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#e65100' }}>⚡ Admin Level 1 Exclusive Control</h4>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#e65100' }}>
              Define marksheet patterns for all classes. Teachers can only enter marks according to your defined structure.
            </p>
            <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#e65100' }}>
              <li>Set subjects for each class</li>
              <li>Configure exam structure (Term 1, Term 2, Half-Yearly, etc.)</li>
              <li>Define maximum marks for each exam type</li>
              <li>Enable/disable specific exam components</li>
            </ul>
          </div>
          <button 
            className="btn" 
            onClick={() => setShowMarksheetTemplateManager(true)}
            style={{ 
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)', 
              color: 'white',
              fontWeight: 'bold',
              padding: '12px 20px',
              fontSize: '16px',
              width: '100%',
              marginBottom: '10px'
            }}
          >
            📋 Manage Marksheet Patterns
          </button>
        </div>

        <div className="card" style={{
          padding: window.innerWidth <= 768 ? '15px' : '20px'
        }}>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
            marginBottom: window.innerWidth <= 768 ? '12px' : '15px'
          }}>📚 Teacher Diary Management</h3>
          <div style={{ marginBottom: '15px', padding: '15px', background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', borderRadius: '10px', border: '2px solid #2196f3' }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#1565c0' }}>⚡ Admin Level 1 Exclusive Control</h4>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#1565c0' }}>
              Configure teacher diary templates for all classes. Set up subjects that teachers can use for assignments.
            </p>
            <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#1565c0' }}>
              <li>Define subjects for each class diary</li>
              <li>Set subject display names</li>
              <li>Enable beautiful diary design with school branding</li>
              <li>Teachers can add assignments with auto serial numbers</li>
            </ul>
          </div>
          <button 
            className="btn" 
            onClick={() => setShowDiaryTemplateManager(true)}
            style={{ 
              background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)', 
              color: 'white',
              fontWeight: 'bold',
              padding: '12px 20px',
              fontSize: '16px',
              width: '100%'
            }}
          >
            📚 Manage Teacher Diary Templates
          </button>
          <button 
            className="btn" 
            onClick={() => setShowWhatsAppLinksManager(true)}
            style={{ 
              background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)', 
              color: 'white',
              fontWeight: 'bold',
              padding: '12px 20px',
              fontSize: '16px',
              width: '100%',
              marginTop: '10px'
            }}
          >
            📱 Manage WhatsApp Links
          </button>
        </div>

        <div className="card" style={{
          padding: window.innerWidth <= 768 ? '15px' : '20px'
        }}>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
            marginBottom: window.innerWidth <= 768 ? '12px' : '15px'
          }}>📊 Reports & Analytics</h3>
          <div style={{ marginBottom: '15px' }}>
            <p><strong>School Statistics:</strong></p>
            <p>Total Students: {users.filter(u => u.role === 'student').length}</p>
            <p>Total Teachers: {users.filter(u => u.role === 'teacher').length}</p>
            <p>Active Classes: {classes.length}</p>
            <p>Average Class Size: {Math.round(users.filter(u => u.role === 'student').length / classes.length)}</p>
          </div>
          <button className="btn" onClick={() => generateReport('Attendance')}>Generate Attendance Report</button>
          <button className="btn" onClick={() => generateReport('Academic Performance')}>Academic Performance Report</button>
          <button className="btn" onClick={() => generateReport('Teacher Performance')}>Teacher Performance Report</button>
        </div>

        <div className="card" style={{
          padding: window.innerWidth <= 768 ? '15px' : '20px'
        }}>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
            marginBottom: window.innerWidth <= 768 ? '12px' : '15px'
          }}>📂 File Upload Management</h3>
          <div style={{ marginBottom: '15px', padding: '15px', background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)', borderRadius: '10px', border: '2px solid #ff9800' }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#e65100' }}>🚫 1MB Upload Limit Enforced</h4>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#e65100' }}>
              All file uploads are now limited to 1MB maximum size for all users (students, teachers, admins).
            </p>
            <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#e65100' }}>
              <li>Profile pictures: 1MB limit</li>
              <li>Content files: 1MB limit</li>
              <li>Document uploads: 1MB limit</li>
              <li>All uploads are tracked and monitored</li>
            </ul>
          </div>
          <div style={{ marginBottom: '15px', padding: '10px', background: '#e8f5e8', borderRadius: '8px', border: '1px solid #4caf50' }}>
            <p style={{ margin: '0', fontSize: '14px', color: '#2e7d32', fontWeight: 'bold' }}>✅ Admin Level 1 File Management Powers:</p>
            <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px', fontSize: '12px', color: '#2e7d32' }}>
              <li>View all uploaded files from all users</li>
              <li>Delete any uploaded file</li>
              <li>Monitor file upload statistics</li>
              <li>Filter files by type and uploader role</li>
            </ul>
          </div>
          <button 
            className="btn" 
            onClick={() => setShowFileManager(true)}
            style={{ 
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)', 
              color: 'white',
              fontWeight: 'bold',
              padding: '12px 20px',
              fontSize: '16px',
              marginBottom: '10px',
              width: '100%'
            }}
          >
            📁 Manage All Uploaded Files
          </button>
          <button className="btn" onClick={viewContent}>View All Content</button>
        </div>



        <div className="card" style={{
          padding: window.innerWidth <= 768 ? '15px' : '20px'
        }}>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
            marginBottom: window.innerWidth <= 768 ? '12px' : '15px'
          }}>📅 Academic Calendar</h3>
          <div style={{ marginBottom: '15px' }}>
            <p><strong>Upcoming Events:</strong></p>
            <div style={{ fontSize: '14px' }}>
              <p>📝 Mid-term Exams: March 15-20</p>
              <p>🏃 Sports Day: March 25</p>
              <p>👨‍👩‍👧‍👦 Parent Meeting: March 30</p>
            </div>
          </div>
          <div style={{ marginBottom: '10px' }}>
            <p><strong>Current Academic Year:</strong></p>
            <div style={{ padding: '8px', background: '#e3f2fd', borderRadius: '6px', textAlign: 'center', marginBottom: '10px' }}>
              <strong style={{ color: '#1565c0', fontSize: '16px' }}>{academicYear}</strong>
            </div>
            <button className="btn" style={{ marginBottom: '5px', width: '100%' }} onClick={updateAcademicYear}>Update Academic Year</button>
          </div>
          <button className="btn" onClick={manageCalendar}>Manage Calendar</button>
          <button className="btn" onClick={scheduleEvent}>Schedule Events</button>
        </div>

        <div className="card" style={{
          padding: window.innerWidth <= 768 ? '15px' : '20px'
        }}>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
            marginBottom: window.innerWidth <= 768 ? '12px' : '15px'
          }}>🏫 School Settings</h3>
          <div style={{ marginBottom: '15px' }}>
            <p><strong>Current School Information:</strong></p>
            <div style={{ padding: '10px', background: '#f8f9fa', borderRadius: '5px', marginBottom: '10px' }}>
              <p><strong>Name:</strong> {localStorage.getItem('schoolName') || 'The Rising School'}</p>
              <p><strong>Address:</strong> {localStorage.getItem('schoolAddress') || 'Savina, Udaipur'}</p>
              <p><strong>Phone:</strong> {localStorage.getItem('schoolPhone') || '8209683549'}</p>
            </div>
          </div>
          <button 
            className="btn" 
            onClick={() => {
              const newName = prompt('School Name:', localStorage.getItem('schoolName') || 'The Rising School');
              const newAddress = prompt('School Address:', localStorage.getItem('schoolAddress') || 'Savina, Udaipur');
              const newPhone = prompt('School Phone:', localStorage.getItem('schoolPhone') || '8209683549');
              
              if (newName !== null) localStorage.setItem('schoolName', newName || 'The Rising School');
              if (newAddress !== null) localStorage.setItem('schoolAddress', newAddress || 'Savina, Udaipur');
              if (newPhone !== null) localStorage.setItem('schoolPhone', newPhone || '8209683549');
              
              alert('School settings updated! Changes will reflect in all ID cards.');
              window.location.reload();
            }}
            style={{ width: '100%', marginBottom: '10px' }}
          >
            ⚙️ Update School Settings
          </button>
        </div>

        <div className="card" style={{
          padding: window.innerWidth <= 768 ? '15px' : '20px'
        }}>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
            marginBottom: window.innerWidth <= 768 ? '12px' : '15px'
          }}>📣 School Announcements</h3>
          <textarea 
            id="school-announcement-textarea"
            placeholder="Write school-wide announcement..." 
            style={{ marginBottom: '10px', minHeight: '80px' }}
          ></textarea>
          <div style={{ marginBottom: '10px' }}>
            <div className="form-group">
              <label>Announcement Title (optional):</label>
              <input type="text" id="announcement-title" placeholder="Enter title..." style={{ marginBottom: '10px' }} />
            </div>
            <label><input type="checkbox" defaultChecked /> Send to all students</label><br/>
            <label><input type="checkbox" defaultChecked /> Send to all teachers</label><br/>
            <label><input type="checkbox" /> Send to parents</label>
          </div>
          <button className="btn" onClick={sendAnnouncement}>Send Announcement</button>
        </div>

        {showMarksheetTemplateManager && (
          <MarksheetTemplateManager 
            onClose={() => setShowMarksheetTemplateManager(false)}
          />
        )}

        {showFileManager && (
          <FileManager 
            onClose={() => setShowFileManager(false)}
          />
        )}

        {showDiaryTemplateManager && (
          <DiaryTemplateManager 
            onClose={() => setShowDiaryTemplateManager(false)}
          />
        )}

        {showWhatsAppLinksManager && (
          <WhatsAppLinksManager 
            onClose={() => setShowWhatsAppLinksManager(false)}
          />
        )}

        {showClassStructureManager && (
          <ClassStructureManager 
            onClose={() => setShowClassStructureManager(false)}
          />
        )}

        {showTuitionStudentManager && selectedTuitionTeacher && (
          <TuitionStudentManager 
            tuitionTeacher={selectedTuitionTeacher}
            allStudents={users.filter(u => u.role === 'student')}
            adminName={user.name}
            onClose={() => {
              setShowTuitionStudentManager(false);
              setSelectedTuitionTeacher(null);
            }}
          />
        )}

        {showTeacherAttendanceView && selectedTeacherForAttendance && (
          <TeacherAttendanceView 
            teacherId={selectedTeacherForAttendance._id}
            teacherName={selectedTeacherForAttendance.name}
            userRole="admin1"
            onClose={() => {
              setShowTeacherAttendanceView(false);
              setSelectedTeacherForAttendance(null);
            }}
          />
        )}

        {showGamesManager && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.5)',
            zIndex: 10000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '10px',
              width: '95%',
              maxWidth: '1200px',
              height: '90%',
              overflow: 'auto',
              position: 'relative'
            }}>
              <button
                onClick={() => setShowGamesManager(false)}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  cursor: 'pointer',
                  zIndex: 10001
                }}
              >
                ×
              </button>
              <GamesManager 
                userRole="admin1" 
                userClass="all" 
              />
            </div>
          </div>
        )}

        {showTimetableManager && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.5)',
            zIndex: 10000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '10px',
              width: '95%',
              maxWidth: '1200px',
              height: '90%',
              overflow: 'auto',
              position: 'relative'
            }}>
              <button
                onClick={() => setShowTimetableManager(false)}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  cursor: 'pointer',
                  zIndex: 10001
                }}
              >
                ×
              </button>
              <TimetableManager 
                userRole="admin1" 
                userId={user.id} 
                userName={user.name} 
              />
            </div>
          </div>
        )}

        {showAcademicStructureManager && (
          <AcademicStructureManager 
            userRole={user.role}
            userName={user.name}
            onClose={() => setShowAcademicStructureManager(false)}
          />
        )}

      </div>
    </div>
  );
}

export default Admin1Dashboard;